import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import { formatRank, TIER_COLORS, TIER_LABELS } from '../utils/arena/rankUtils';
import { getCrownsForUser } from '../utils/arena/hallOfFameCache';
import AvatarCrownBadge from './AvatarCrownBadge';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  DIALOG_STYLE, FourCorners, BEVELED_BTN, GOLD_BTN,
} from '../lib/medievalTheme';
import { cn } from '../lib/utils';
import { k, t } from '../localization/t';
import {
  PopoverPanel, PopoverMenuItem, PopoverMenuDivider, PopoverMenuHeader, SubmenuChevron,
} from './ui/popoverMenu';

// Inline SVG icons. Clean stroke-based shapes matching the line-icon
// language the rest of the dock uses. No emoji: emoji render with
// per-platform color styling that breaks the dark-glass aesthetic
// and reads as 2000s web UI on anything modern.
const IconEdit = (props) => (
  <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const IconUser = (props) => (
  <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconMessage = (props) => (
  <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconRemove = (props) => (
  <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// Modern action button. Clean rounded rect with a thin gold border,
// subtle fill, and hover/press states driven by a single rAF-style
// inline handler. Replaces the old chiseled-stone BEVELED_BTN which
// read as 2000s JRPG. Variants:
//   - default  : neutral panel, body-text colour
//   - primary  : gold accent, the "hero" action (Send Message)
//   - danger   : red text, same chrome as default (destructive)
function ActionButton({ icon, label, onClick, variant = 'default' }) {
  const baseBg = variant === 'primary'
    ? `linear-gradient(180deg, rgba(212,168,67,0.95) 0%, rgba(160,120,40,0.95) 100%)`
    : 'rgba(255,255,255,0.03)';
  const hoverBg = variant === 'primary'
    ? `linear-gradient(180deg, rgba(228,185,88,1) 0%, rgba(180,140,56,1) 100%)`
    : 'rgba(255,255,255,0.06)';
  const pressBg = variant === 'primary'
    ? `linear-gradient(180deg, rgba(188,148,50,1) 0%, rgba(140,100,28,1) 100%)`
    : 'rgba(255,255,255,0.02)';
  const color = variant === 'primary' ? '#1a1408' : variant === 'danger' ? '#e07070' : TEXT_PRIMARY;
  const borderColor = variant === 'primary'
    ? 'rgba(228,185,88,0.55)'
    : variant === 'danger'
      ? 'rgba(220,80,80,0.22)'
      : `${GOLD} 0.2)`;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center gap-2 flex-1 min-w-0 px-3 py-2.5 text-[0.75cqw] font-medium rounded-lg transition-colors cursor-pointer"
      style={{
        color,
        background: baseBg,
        border: `1px solid ${borderColor}`,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = baseBg; }}
      onMouseDown={(e) => { e.currentTarget.style.background = pressBg; }}
      onMouseUp={(e) => { e.currentTarget.style.background = hoverBg; }}
    >
      <span className="shrink-0" style={{ color }}>{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}

// Floating mini profile popover. One component drives both the
// self variant (clicked from the presence bar's left avatar) and
// the other-user variant (hovered in a friend list). Same shape,
// different action buttons.
//
// Positioning: caller passes `anchor` with { bottom, left } in
// viewport-space pixels. The popover anchors its bottom-left to
// that point. Hover-triggered popovers compute `anchor` from the
// triggering element's getBoundingClientRect.

// Discord-inspired personality fields. Bio + pronouns are user-edited
// free-form text. customStatus used to be free-form too but is now
// auto-derived from the user's current activity (managed by
// presenceManager.updateActivity), so it's read-only at the UI layer.
const BIO_MAX = 200;
const PRONOUNS_MAX = 40;

// Presence (availability). Matches Discord's four-option model.
// Online = green, idle = amber, dnd = red, invisible = grey-seen-as-
// offline-to-others. Each gets a small colored dot on the avatar.
const PRESENCE_STATUSES = [
  { key: 'online',    label: k('Online'),           color: '#6ac084', caption: '' },
  { key: 'idle',      label: k('Idle'),             color: '#e0a060', caption: k('Away from keyboard') },
  { key: 'dnd',       label: k('Do Not Disturb'),   color: '#c45050', caption: k('Mutes notifications') },
  { key: 'invisible', label: k('Invisible'),        color: '#8a8179', caption: k('You will appear offline') },
];

// Duration choices for a non-online status. `ms: null` means "until
// manually changed" (Discord calls this "Forever" in the duration
// picker, but the label reads better here).
const PRESENCE_DURATIONS = [
  { key: '15m',   label: k('For 15 Minutes'), ms: 15 * 60 * 1000 },
  { key: '1h',    label: k('For 1 Hour'),     ms: 60 * 60 * 1000 },
  { key: '8h',    label: k('For 8 Hours'),    ms: 8 * 60 * 60 * 1000 },
  { key: '24h',   label: k('For 24 Hours'),   ms: 24 * 60 * 60 * 1000 },
  { key: '3d',    label: k('For 3 Days'),     ms: 3 * 24 * 60 * 60 * 1000 },
  { key: 'forever', label: k('Until I change it'), ms: null },
];

function getPresenceMeta(key) {
  return PRESENCE_STATUSES.find((s) => s.key === key) || PRESENCE_STATUSES[0];
}

export default class MiniProfile extends Component {
  state = {
    editing: false,
    draftBio: '',
    draftPronouns: '',
    saving: false,
    // Presence sub-popover: null | 'status' (picking availability) |
    // one of 'idle'/'dnd'/'invisible' (picking duration for that
    // status). Online is applied immediately so it has no duration
    // stage.
    presenceStage: null,
    // Viewport-aware placement for the side-popouts. `true` = grow
    // upward (menu's bottom edge aligns with its trigger's bottom);
    // `false` = grow downward (menu's top edge aligns with trigger's
    // top). Default to growing upward because the mini profile lives
    // above the call dock. It's almost always in the lower half of
    // the viewport. And correct for the rare opposite case after
    // measuring in componentDidUpdate.
    statusMenuFlip: true,
    durationMenuFlip: true,
    crowns: [],
  };

  statusItemRefs = {};

  componentDidMount() {
    if (this.props.open && this.props.profile?.id) this.loadCrowns(this.props.profile.id);
  }

  componentDidUpdate(prevProps) {
    // Exit edit mode when the popover closes or switches target.
    if (prevProps.open && !this.props.open) {
      if (this.state.editing || this.state.presenceStage) {
        this.setState({ editing: false, presenceStage: null });
      }
    }
    if (prevProps.profile?.id !== this.props.profile?.id) {
      if (this.state.editing) this.setState({ editing: false, presenceStage: null });
      if (this.props.profile?.id) this.loadCrowns(this.props.profile.id);
      else this.setState({ crowns: [] });
    } else if (!prevProps.open && this.props.open && this.props.profile?.id) {
      this.loadCrowns(this.props.profile.id);
    }
    this.updateSubmenuPlacement();
  }

  async loadCrowns(profileId) {
    try {
      const crowns = await getCrownsForUser(profileId, this.props.sorceryCards);
      if (this.props.profile?.id === profileId) this.setState({ crowns });
    } catch (err) {
      console.warn('[MiniProfile] Failed to load crowns:', err);
    }
  }

  // Runs after every render. Measures the visible submenus against the
  // viewport and flips placement (grow-up vs grow-down) so neither
  // panel ever spills off the bottom / top of the screen. Guards with
  // identity checks to avoid setState feedback loops.
  updateSubmenuPlacement = () => {
    const { presenceStage, statusMenuFlip, durationMenuFlip } = this.state;
    if (!presenceStage) return;
    const vh = window.innerHeight;
    const margin = 8;
    const patch = {};

    if (this.presenceTriggerRef && this.statusMenuRef) {
      const trig = this.presenceTriggerRef.getBoundingClientRect();
      const menu = this.statusMenuRef.getBoundingClientRect();
      const h = menu.height || 200;
      const fitsDown = trig.top + h <= vh - margin;
      const fitsUp = trig.bottom - h >= margin;
      // Prefer "grow up" when both fit (matches default). This keeps
      // the submenu visually attached to the trigger row instead of
      // covering the rows below it.
      const flip = fitsUp ? !fitsDown ? true : statusMenuFlip : false;
      if (flip !== statusMenuFlip) patch.statusMenuFlip = flip;
    }

    if (presenceStage !== 'status' && this.durationMenuRef) {
      const trigEl = this.statusItemRefs?.[presenceStage];
      if (trigEl) {
        const trig = trigEl.getBoundingClientRect();
        const menu = this.durationMenuRef.getBoundingClientRect();
        const h = menu.height || 260;
        const fitsDown = trig.top + h <= vh - margin;
        const fitsUp = trig.bottom - h >= margin;
        const flip = fitsUp ? !fitsDown ? true : durationMenuFlip : false;
        if (flip !== durationMenuFlip) patch.durationMenuFlip = flip;
      }
    }

    if (Object.keys(patch).length) this.setState(patch);
  };

  openPresence = () => this.setState({ presenceStage: 'status' });

  pickPresence = (statusKey) => {
    if (statusKey === 'online') {
      // Online needs no duration. Apply instantly.
      this.props.onSavePresence?.({ status: 'online', expiresAt: null });
      this.setState({ presenceStage: null });
      return;
    }
    this.setState({ presenceStage: statusKey });
  };

  pickPresenceDuration = (statusKey, ms) => {
    const expiresAt = ms ? Date.now() + ms : null;
    this.props.onSavePresence?.({ status: statusKey, expiresAt });
    this.setState({ presenceStage: null });
  };

  // Discord-style side-popout submenus. Dark-glass panel, thin
  // gold border, radio-dot selected state, hover lightens. The
  // status submenu anchors to the presence trigger row; selecting
  // a non-Online status chains a duration submenu further to the
  // right off whichever status row is active. Placement (grow-up
  // vs grow-down) is measured in updateSubmenuPlacement so neither
  // panel ever overflows the viewport edge.
  renderPresenceSubmenu(currentPresenceKey) {
    const { presenceStage, statusMenuFlip, durationMenuFlip } = this.state;
    const activeDurationKey = presenceStage !== 'status' ? presenceStage : null;
    const anchorEdge = statusMenuFlip ? { bottom: 0 } : { top: 0 };

    return (
      <PopoverPanel
        innerRef={(el) => { this.statusMenuRef = el; }}
        style={{
          position: 'absolute',
          left: '100%',
          marginLeft: 6,
          minWidth: '15cqw',
          zIndex: 10,
          ...anchorEdge,
        }}
      >
        {PRESENCE_STATUSES.map((s, i) => {
          const hasDuration = s.key !== 'online';
          const isArmed = activeDurationKey === s.key;
          return (
            <div
              key={s.key}
              className="relative"
              ref={(el) => { this.statusItemRefs[s.key] = el; }}
            >
              {i > 0 ? <PopoverMenuDivider /> : null}
              <PopoverMenuItem
                label={t(s.label)}
                caption={s.caption ? t(s.caption) : s.caption}
                selected={currentPresenceKey === s.key}
                active={isArmed}
                leading={(
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ background: s.color }}
                  />
                )}
                trailing={hasDuration ? <SubmenuChevron /> : null}
                onClick={() => this.pickPresence(s.key)}
              />

              {hasDuration && isArmed ? (
                <PopoverPanel
                  innerRef={(el) => { this.durationMenuRef = el; }}
                  style={{
                    position: 'absolute',
                    left: '100%',
                    marginLeft: 6,
                    minWidth: '12.5cqw',
                    zIndex: 11,
                    ...(durationMenuFlip ? { bottom: 0 } : { top: 0 }),
                  }}
                >
                  <PopoverMenuHeader>{t(s.label)}</PopoverMenuHeader>
                  {PRESENCE_DURATIONS.map((d, j) => (
                    <>
                      {j > 0 ? <PopoverMenuDivider /> : null}
                      <PopoverMenuItem
                        key={d.key}
                        label={t(d.label)}
                        onClick={() => this.pickPresenceDuration(s.key, d.ms)}
                      />
                    </>
                  ))}
                </PopoverPanel>
              ) : null}
            </div>
          );
        })}
      </PopoverPanel>
    );
  }

  beginEdit = () => {
    const p = this.props.profile || {};
    this.setState({
      editing: true,
      draftBio: p.bio || '',
      draftPronouns: p.pronouns || '',
    });
  };

  cancelEdit = () => this.setState({ editing: false });

  saveEdit = async () => {
    if (this.state.saving) return;
    this.setState({ saving: true });
    try {
      await this.props.onSavePersonality?.({
        bio: this.state.draftBio,
        pronouns: this.state.draftPronouns,
      });
      this.setState({ editing: false });
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const {
      open, profile, isSelf, sorceryCards, anchor,
      onClose, onEditProfile, onViewFullProfile, onSendMessage, onRemoveFriend,
    } = this.props;

    if (!profile) return null;

    const avatarUrl = resolveAvatarUrl(profile, sorceryCards);
    const initial = (profile.name || profile.displayName || 'U')[0]?.toUpperCase() || '?';
    // Presence resolves to 'offline' for a friend who isn't online,
    // otherwise whatever status the user picked (defaults to online).
    // Self is always at least "online" from their own perspective.
    const presenceKey = isSelf
      ? (profile.presenceStatus || 'online')
      : (profile.online ? (profile.presenceStatus || 'online') : 'offline');
    const presence = getPresenceMeta(presenceKey);
    const presenceDot = presenceKey === 'offline' ? '#6b6158' : presence.color;
    const bioText = profile.bio || null;
    const pronounsText = profile.pronouns || null;
    const statusText = profile.customStatus || null;
    const { editing, draftBio, draftPronouns, saving, presenceStage, crowns } = this.state;
    // The displayed crown follows the player's stored choice, or the
    // highest-wins crown when no choice is stored. Falls back silently
    // when the stored avatar is no longer one they currently hold.
    const chosenAvatarId = profile.displayedCrownAvatarId || null;
    const displayedCrown = (chosenAvatarId && crowns.find((c) => c.avatarId === chosenAvatarId)) || crowns[0] || null;

    // Bottom-anchored popover. Caller supplies viewport-space coords;
    // when a `width` is given (e.g. the call dock passes its own
    // width) we match it so the popover reads as an upward extension
    // of the trigger rather than a detached floating card.
    const posStyle = anchor
      ? {
          bottom: anchor.bottom ?? 64,
          left: anchor.left ?? 16,
        }
      : {
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
        };
    const popoverWidth = anchor?.width ?? 320;

    // Stats used by the widget row when this is a full profile (self
    // or friend). Mirrors what FriendProfileOverlay surfaces, just
    // condensed. Safe defaults keep the layout stable when a field
    // hasn't loaded yet.
    const wins = profile.wins || 0;
    const losses = profile.losses || 0;
    const totalMatches = wins + losses;
    const winRate = totalMatches > 0 ? Math.round((wins / totalMatches) * 100) : 0;
    const uniqueCards = profile.uniqueCards || 0;
    const totalCollection = profile.collectionSize || 0;
    const rankTier = profile.rank?.tier;
    const rankDivision = profile.rank?.division;
    const rankLp = profile.rank?.lp || 0;
    const rankDivisionProgress = Math.min(100, Math.max(0, rankLp));
    const tierLabel = TIER_LABELS?.[rankTier] || rankTier || t('Unranked');
    const rankColorClass = TIER_COLORS[rankTier] || 'text-white';

    return (
      <AnimatePresence>
        {open ? (
          <>
            {/* Transparent click-shield so any outside click closes
                the popover. No visible backdrop. This is an ambient
                widget, not a modal. */}
            <div
              className="fixed inset-0 z-[78]"
              onClick={onClose}
              style={{ background: 'transparent' }}
            />
            <motion.div
              className="fixed z-[79] flex flex-col pointer-events-auto"
              data-screenshot-ignore="true"
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
              style={{
                ...posStyle,
                width: popoverWidth,
                // No CSS zoom on the popover itself. The anchor
                // coords (bottom/left/width) are already in
                // viewport-space (getBoundingClientRect on the
                // zoomed dock returns post-zoom values), so applying
                // another viewport-scale here would re-scale those
                // coords and push the popover off to the side.
                // Sizing is driven by the dock's rect so it still
                // matches the dock's visual width at any zoom.
                ...DIALOG_STYLE,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <FourCorners />

              {/* Avatar. Split into two layers: the outer wrapper is
                  relative but un-clipped so the presence dot overlaps
                  the rim cleanly; the inner ring clips the portrait
                  itself into a circle. Clicking the avatar (when an
                  onViewFullProfile handler is wired) navigates to the
                  full profile. Same gesture as clicking the name
                  below, so the entire identity block reads as a link. */}
              <div className="relative px-5 pt-5 pb-1">
                <button
                  type="button"
                  className="relative cursor-pointer"
                  style={{
                    width: '7cqw',
                    height: '7cqw',
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '50%',
                  }}
                  onClick={() => { if (onViewFullProfile) { onViewFullProfile(); onClose?.(); } }}
                  title={t('View full profile')}
                  disabled={!onViewFullProfile}
                >
                  <div
                    className="w-full h-full rounded-full overflow-hidden flex items-center justify-center"
                    style={{
                      background: 'rgba(14,12,8,0.95)',
                      border: `3px solid rgba(14,12,8,0.95)`,
                      boxShadow: '0 6px 16px rgba(0,0,0,0.6)',
                    }}
                  >
                    {avatarUrl ? (
                      <AvatarImage src={avatarUrl} className="w-full h-full" />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-3xl font-bold"
                        style={{ background: `${GOLD} 0.15)`, color: TEXT_BODY }}
                      >
                        {initial}
                      </div>
                    )}
                  </div>
                  <span
                    className="absolute rounded-full"
                    style={{
                      width: '1.5cqw',
                      height: '1.5cqw',
                      right: 2,
                      bottom: 2,
                      background: presenceDot,
                      border: '3px solid rgba(14,12,8,0.95)',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
                    }}
                  />
                </button>
              </div>

              <div className="flex flex-col px-5 pt-3 pb-4 gap-2.5">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <button
                    type="button"
                    className="text-lg font-bold arena-heading cursor-pointer"
                    style={{
                      color: TEXT_PRIMARY,
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: onViewFullProfile ? 'pointer' : 'default',
                    }}
                    onClick={() => { if (onViewFullProfile) { onViewFullProfile(); onClose?.(); } }}
                    onMouseEnter={(e) => { if (onViewFullProfile) e.currentTarget.style.color = ACCENT_GOLD; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_PRIMARY; }}
                    disabled={!onViewFullProfile}
                    title={onViewFullProfile ? t('View full profile') : undefined}
                  >
                    {profile.name || profile.displayName || t('Unknown')}
                  </button>
                  {/* Inline crown strip. Sits next to the name. Tiny
                      medallions, no labels. Display-only. Title is
                      changed exclusively via the full profile's Edit
                      Profile → Title tab, so this strip carries no
                      click handler in either self or friend mode. */}
                  {crowns.length > 0 ? (
                    <span
                      className="flex items-end gap-1 px-1 py-0.5"
                      title={displayedCrown?.label}
                    >
                      {crowns.slice(0, 4).map((crown) => (
                        <span
                          key={crown.avatarId}
                          style={{
                            outline: crown.avatarId === displayedCrown?.avatarId ? `1px solid ${ACCENT_GOLD}` : 'none',
                            outlineOffset: '1.5px',
                            borderRadius: '50%',
                          }}
                        >
                          <AvatarCrownBadge crown={crown} size="1.25cqw" showTooltip={false} />
                        </span>
                      ))}
                      {crowns.length > 4 ? (
                        <span className="text-[0.5cqw] tabular-nums italic self-end ml-0.5" style={{ color: ACCENT_GOLD }}>
                          +{crowns.length - 4}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                  {pronounsText && !editing ? (
                    <span
                      className="text-[0.625cqw] px-1.5 py-0.5 rounded"
                      style={{ color: TEXT_MUTED, background: `${GOLD} 0.08)` }}
                    >
                      {pronounsText}
                    </span>
                  ) : null}
                  {rankTier ? (
                    <span className="text-[0.625cqw] ml-auto" style={{ color: ACCENT_GOLD }}>
                      {formatRank(rankTier, rankDivision)}
                    </span>
                  ) : null}
                </div>
                {/* Italic title under the name. Single line, only when
                    a crown is currently displayed. Format mirrors the
                    full-profile hero: "Crown of X · subtitle." */}
                {displayedCrown ? (
                  <span className="text-[0.625cqw] italic truncate -mt-1" style={{ color: ACCENT_GOLD }}>
                    {displayedCrown.label}{displayedCrown.title ? ` · ${displayedCrown.title}` : ''}
                  </span>
                ) : null}
                {profile.username ? (
                  <span className="text-[0.6875cqw] -mt-1" style={{ color: TEXT_MUTED }}>
                    {profile.username}
                  </span>
                ) : null}
                {statusText && !editing ? (
                  <div
                    className="flex items-center gap-1.5 text-[0.6875cqw] self-start"
                    style={{ color: TEXT_BODY }}
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: ACCENT_GOLD }}
                    />
                    <span className="truncate">{statusText}</span>
                  </div>
                ) : null}
                {bioText && !editing ? (
                  <p
                    className="text-[0.6875cqw] leading-relaxed"
                    style={{ color: TEXT_BODY, whiteSpace: 'pre-wrap' }}
                  >
                    {bioText}
                  </p>
                ) : null}

                {/* Edit form. Shown only for self + when user clicked
                    the pencil. Three Discord-style personality fields:
                    a free-form bio, short pronouns tag, and a custom
                    status ("what you're up to"). */}
                {isSelf && editing ? (
                  <div className="flex flex-col gap-2 mt-1">
                    <label className="flex flex-col gap-1">
                      <span className="text-[0.5625cqw] font-semibold" style={{ color: ACCENT_GOLD }}>
                        {t('About Me')}
                      </span>
                      <textarea
                        className="px-2 py-1.5 text-[0.6875cqw] rounded resize-none"
                        style={{
                          background: 'rgba(0,0,0,0.3)',
                          border: `1px solid ${GOLD} 0.2)`,
                          color: TEXT_BODY,
                          outline: 'none',
                          minHeight: '3.5cqw',
                          lineHeight: '16px',
                        }}
                        placeholder={t('What games do you enjoy? Favourite deck? Tell other players about yourself.')}
                        maxLength={BIO_MAX}
                        value={draftBio}
                        onInput={(e) => this.setState({ draftBio: e.target.value })}
                      />
                      <span className="text-[0.5625cqw] self-end" style={{ color: TEXT_MUTED }}>
                        {draftBio.length}/{BIO_MAX}
                      </span>
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[0.5625cqw] font-semibold" style={{ color: ACCENT_GOLD }}>
                        {t('Pronouns')}
                      </span>
                      <input
                        className="px-2 py-1.5 text-[0.6875cqw] rounded"
                        style={{
                          background: 'rgba(0,0,0,0.3)',
                          border: `1px solid ${GOLD} 0.2)`,
                          color: TEXT_BODY,
                          outline: 'none',
                        }}
                        placeholder={t('e.g. she/her, they/them')}
                        maxLength={PRONOUNS_MAX}
                        value={draftPronouns}
                        onInput={(e) => this.setState({ draftPronouns: e.target.value })}
                      />
                    </label>
                    <div className="flex justify-end gap-2 mt-1">
                      <button
                        type="button"
                        className="px-3 py-1 text-[0.625cqw] rounded cursor-pointer"
                        style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-sm)' }}
                        onClick={this.cancelEdit}
                        disabled={saving}
                      >
                        {t('Cancel')}
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 text-[0.625cqw] font-semibold rounded cursor-pointer"
                        style={{ ...GOLD_BTN, color: '#0e0a06', borderRadius: 'var(--radius-sm)' }}
                        onClick={this.saveEdit}
                        disabled={saving}
                      >
                        {saving ? t('Saving…') : t('Save')}
                      </button>
                    </div>
                  </div>
                ) : null}
                {profile.coins != null || profile.arcanaShards != null ? (
                  <div className="flex items-center gap-3 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                    {profile.coins != null ? (
                      <span>
                        <span style={{ color: '#f0d060' }}>{profile.coins}</span> {t('gold')}
                      </span>
                    ) : null}
                    {profile.arcanaShards != null ? (
                      <span>
                        <span style={{ color: '#7dd3fc' }}>{profile.arcanaShards}</span> {t('arcana')}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {/* Condensed stat widgets. A smaller version of the
                  FriendProfileOverlay panels. Only render the groups
                  that have data so a brand-new account doesn't show
                  a row of zeroes that look broken. */}
              {(totalMatches > 0 || rankTier || uniqueCards > 0) ? (
                <div
                  className="flex flex-col gap-2.5 px-5 py-4"
                  style={{ borderTop: `1px solid ${GOLD} 0.1)`, borderBottom: `1px solid ${GOLD} 0.1)` }}
                >
                  {totalMatches > 0 ? (
                    <div className="flex items-center justify-between text-[0.625cqw]">
                      <span style={{ color: TEXT_MUTED }}>{t('Matches')}</span>
                      <span className="tabular-nums" style={{ color: TEXT_BODY }}>
                        <span style={{ color: '#6ab04c' }}>{t('{n}W', { n: wins })}</span>
                        <span className="mx-1" style={{ color: TEXT_MUTED }}>·</span>
                        <span style={{ color: '#c45050' }}>{t('{n}L', { n: losses })}</span>
                        <span className="mx-1" style={{ color: TEXT_MUTED }}>·</span>
                        <span style={{ color: TEXT_PRIMARY }}>{winRate}%</span>
                      </span>
                    </div>
                  ) : null}
                  {rankTier ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between text-[0.625cqw]">
                        <span style={{ color: TEXT_MUTED }}>{t('Rank')}</span>
                        <span className={cn('font-semibold', rankColorClass)}>
                          {formatRank(rankTier, rankDivision)}
                        </span>
                      </div>
                      <div
                        className="h-1 rounded-full overflow-hidden"
                        style={{ background: 'rgba(212,168,67,0.12)' }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${rankDivisionProgress}%`,
                            background: `linear-gradient(90deg, #8b6914, ${ACCENT_GOLD}, #c49a38)`,
                          }}
                        />
                      </div>
                    </div>
                  ) : null}
                  {(uniqueCards > 0 || totalCollection > 0) ? (
                    <div className="flex items-center justify-between text-[0.625cqw]">
                      <span style={{ color: TEXT_MUTED }}>{t('Collection')}</span>
                      <span className="tabular-nums" style={{ color: TEXT_BODY }}>
                        {t('{n} unique', { n: uniqueCards })} <span style={{ color: TEXT_MUTED }}>·</span> {t('{n} total', { n: totalCollection })}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Presence selector. Self only. Single row in the
                  main popover shows the current status. Click it and
                  a submenu pops out to the RIGHT of the main popover
                  with the four availability choices. Clicking a
                  non-Online choice pops a second submenu to the
                  right of that with duration options. Same side-by-
                  side layering Discord uses. No inline accordion,
                  main card content stays visible the whole time. */}
              {isSelf && !editing ? (
                <div
                  className="relative px-4 py-2"
                  style={{ borderTop: `1px solid ${GOLD} 0.1)` }}
                >
                  <button
                    ref={(el) => { this.presenceTriggerRef = el; }}
                    type="button"
                    className="flex items-center gap-2.5 w-full px-3 py-2 rounded-md transition-colors cursor-pointer"
                    style={{
                      background: presenceStage ? 'rgba(255,255,255,0.05)' : 'transparent',
                      color: TEXT_PRIMARY,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    onMouseLeave={(e) => { if (!presenceStage) e.currentTarget.style.background = 'transparent'; }}
                    onClick={() => this.setState({ presenceStage: presenceStage ? null : 'status' })}
                  >
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ background: presence.color, boxShadow: `0 0 6px ${presence.color}` }}
                    />
                    <span className="text-[0.75cqw] font-medium">{t(presence.label)}</span>
                    <span className="ml-auto" style={{ color: TEXT_MUTED }}><SubmenuChevron /></span>
                  </button>

                  {presenceStage ? this.renderPresenceSubmenu(presenceKey) : null}
                </div>
              ) : null}

              {/* Actions row. Horizontal. Each button fills equal
                  width via flex-1 so the row feels intentional. */}
              <div
                className="shrink-0 flex gap-2 px-4 pb-4 pt-3"
                style={{ borderTop: `1px solid ${GOLD} 0.1)` }}
              >
                {isSelf ? (
                  <>
                    {!editing ? (
                      <ActionButton
                        icon={<IconEdit />}
                        label={t('Edit')}
                        onClick={this.beginEdit}
                      />
                    ) : null}
                    {onViewFullProfile && !editing ? (
                      <ActionButton
                        icon={<IconUser />}
                        label={t('Full Profile')}
                        onClick={() => { onViewFullProfile(); onClose?.(); }}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    {onSendMessage ? (
                      <ActionButton
                        variant="primary"
                        icon={<IconMessage />}
                        label={t('Message')}
                        onClick={() => { onSendMessage(); onClose?.(); }}
                      />
                    ) : null}
                    {onViewFullProfile ? (
                      <ActionButton
                        icon={<IconUser />}
                        label={t('Profile')}
                        onClick={() => { onViewFullProfile(); onClose?.(); }}
                      />
                    ) : null}
                    {onRemoveFriend ? (
                      <ActionButton
                        variant="danger"
                        icon={<IconRemove />}
                        label={t('Remove')}
                        onClick={() => { onRemoveFriend(); onClose?.(); }}
                      />
                    ) : null}
                  </>
                )}
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    );
  }
}

